import React, { useMemo, useRef, useEffect } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import amplitude from 'amplitude-js';
import { toggleModal, toggleSpinner } from 'apps/auth/actions/appAction';
import { addExtras } from 'apps/auth/actions/extraAction';
import { changeFormFieldValue, setNewFields } from 'apps/auth/actions/formAction';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import CashPickupLocationsPopup from 'apps/auth/components/send/popups/CashPickupLocationsPopup';
import { getRecipientTypeParam, transactionMethods } from 'apps/auth/components/utils/AmplitudeUtils';
import { PAYOUT_METHOD_GUIDE_BUTTON_CLICKED, PAYOUT_NETWORK_SELECTED, PAYOUT_NETWORKS_LIST_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { NETWORK, PAYOUT_COUNTRY, PAYOUT_METHOD, RECIPIENT_TYPE } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { DELIVERY_CASH_UNITELLER } from 'apps/auth/constants/Constants';
import { SOURCE_CODE_TO } from 'apps/auth/constants/fieldsName';
import { SEND_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_SEND } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import update from 'apps/auth/helpers/Immutability';
import { toLowerCase } from 'apps/auth/helpers/utils';
import { useExtra, useFields, usePageMessages } from 'apps/auth/hooks/selectors';

import SubSource from './SubSource';
import NewDisclaimer from '../../disclaimers/NewDisclaimer';

const ToSubSources = (props) => {
    const { toCountry, toCurrency, deliveryMethod, showButtonHowDoesItWork } = useExtra();
    const { subSourceScreenTitle, subSourceScreenSubTitle, howDoesItWork, badgePopular, badgeOtherOptions } = usePageMessages();
    const fields = useFields(SEND_FORM_NAME);
    const popupRef = useRef(null);
    const amplitudeDeliveryMethod = transactionMethods[deliveryMethod];
    const sortedSource = useMemo(() => {
        const sortedSource = {
            popular: [],
            showPopular: false,
            other: [],
            showOther: false,
        };
        props.subSource.forEach((source) => {
            if (source.categoryOrder < 0) {
                sortedSource.popular.push(source);
            } else {
                sortedSource.other.push(source);
            }
        });
        if (sortedSource.popular.length > 0) {
            sortedSource.popular.sort((a, b) => a - b);
            sortedSource.showPopular = true;
        }
        if (sortedSource.other.length > 0) {
            sortedSource.other.sort((a, b) => a - b);
            sortedSource.showOther = true;
        }
        return sortedSource;
    }, []);

    useEffect(() => {
        if (__isBrowser__) {
            props.sendAmplitudeEventOnRender?.(PAYOUT_NETWORKS_LIST_SHOWN);
        }
    }, []);

    const showDisclaimer = (e, source) => {
        e?.preventDefault();
        props.toggleModal(
            true,
            <NewDisclaimer
                detailsClick={(e, bottomSheetBanks) => {
                    e?.preventDefault?.();
                    popupRef?.current?.open(<CashPickupLocationsPopup popupRef={popupRef} banksList={bottomSheetBanks?.banks} />);
                }}
                clickAgree={(e) => {
                    if (source) {
                        selectSubSource(e, source, false);
                    }
                    props.toggleModal(false);
                }}
                recipientType={props.typeOfTransfer}
            />,
        );
    };

    const selectSubSource = (e, source, isShowDisclaimer) => {
        amplitude.getInstance().logEvent(PAYOUT_NETWORK_SELECTED, {
            [NETWORK]: source?.sourceName,
            [RECIPIENT_TYPE]: getRecipientTypeParam(props.typeOfTransfer),
        });
        if (isShowDisclaimer) {
            showDisclaimer(e, source);
        } else {
            props.disclaimerWasShown();
            e?.preventDefault();
            const url = `${AUTH_SEND}/${toLowerCase(toCountry?.code)}/${toLowerCase(toCurrency?.code)}/${deliveryMethod}${props.sendUriPostfix}`;
            props.changeFormFieldValue(SEND_FORM_NAME, source.code, SOURCE_CODE_TO).then(() => {
                props.toggleSpinner(true);
                const newFields = update(fields, {
                    [SOURCE_CODE_TO]: {
                        value: { $set: source.code },
                    },
                });
                apiFetchData(url, newFields)
                    .then((json) => {
                        const form = json?.forms[SEND_FORM_NAME];
                        const responseFields = form?.fields;
                        if (json.extra) {
                            props.addExtras(json.extra);
                        }
                        if (json?.redirectTo) {
                            window.location.replace(json.redirectTo);
                        } else if (responseFields) {
                            props.setNewFields(SEND_FORM_NAME, responseFields).then(() => {
                                props.setNextScreen();
                            });
                        }
                    })
                    .catch((err) => logWarn(err))
                    .finally(() => {
                        props.toggleSpinner(false);
                    });
            });
        }
    };

    return (
        <div>
            <HeaderPanel
                title={subSourceScreenTitle}
                subtitle={subSourceScreenSubTitle}
                buttonRowCaption={showButtonHowDoesItWork ? howDoesItWork : ''}
                buttonRowOnClick={(e) => {
                    if (!showButtonHowDoesItWork) {
                        return;
                    }
                    amplitude.getInstance().logEvent(PAYOUT_METHOD_GUIDE_BUTTON_CLICKED, {
                        [PAYOUT_COUNTRY]: toCountry?.nameEn,
                        [PAYOUT_METHOD]: amplitudeDeliveryMethod,
                        [RECIPIENT_TYPE]: getRecipientTypeParam(props.typeOfTransfer),
                    });
                    showDisclaimer(e);
                }}
                buttonRowType="button"
                buttonRowSizeFit
            />
            {sortedSource.showPopular && sortedSource.showOther && (
                <Row indent="48px">
                    <Text variant="div" size="subheader" color="secondary" indent="adaptive-16-24">
                        {badgePopular}
                    </Text>
                    {sortedSource.popular.map((source) => (
                        <SubSource
                            key={source.prvId}
                            source={source}
                            isShowDisclaimer={props.isShowDisclaimer && deliveryMethod === DELIVERY_CASH_UNITELLER}
                            selectSubSource={selectSubSource}
                        />
                    ))}
                </Row>
            )}

            {sortedSource.showPopular && sortedSource.showOther && (
                <Text variant="div" size="subheader" color="secondary" indent="adaptive-16-24">
                    {badgeOtherOptions}
                </Text>
            )}
            {sortedSource.showOther &&
                sortedSource.other.map((source) => (
                    <SubSource
                        key={source.prvId}
                        source={source}
                        isShowDisclaimer={props.isShowDisclaimer && deliveryMethod === DELIVERY_CASH_UNITELLER}
                        selectSubSource={selectSubSource}
                    />
                ))}
            <PopupNew ref={popupRef} />
        </div>
    );
};

ToSubSources.propTypes = {
    isShowDisclaimer: PropTypes.bool.isRequired,
    disclaimerWasShown: PropTypes.func.isRequired,
};

export default connect(null, { changeFormFieldValue, setNewFields, addExtras, toggleSpinner, toggleModal })(ToSubSources);
